import type {
  AdminCronEmailVerificationAction as AdminCronEmailVerificationActionModel,
  AdminCronParamValues,
  AdminCronVariant,
} from "../../models/admin-cron.models";
import { adminStyles } from "../../styles/admin.styles";
import { ADMIN_CRON_CARGILL_EMAIL_ERROR } from "../../utils/admin-cron.constants";
import { normalizeAdminCronCargillEmail } from "../../utils/admin-cron.utils";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import type { FormEvent } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  action: AdminCronEmailVerificationActionModel;
  isRunning: boolean;
  isSending: boolean;
  onSend: (
    variant: AdminCronVariant,
    values: AdminCronParamValues,
    parameters: AdminCronEmailVerificationActionModel["param"][],
    confirmationMessage: string,
  ) => void;
}

const CronEmailVerificationAction = ({ action, isRunning, isSending, onSend }: Props) => {
  const [email, setEmail] = useState("");
  const normalizedEmail = normalizeAdminCronCargillEmail(email);
  const showValidationError = email.trim().length > 0 && normalizedEmail === null;
  const errorId = `admin-cron-${action.variant.id}-email-error`;
  const inputId = `admin-cron-${action.variant.id}-email`;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!normalizedEmail) return;

    onSend(
      action.variant,
      { [action.param.key]: normalizedEmail },
      [action.param],
      `Send ${action.emailCount} synthetic Crew Report test emails to ${normalizedEmail}?`,
    );
  };

  return (
    <section
      className="mb-5 bg-white px-5 py-4 shadow-sm dark:bg-gray-800"
      data-test="admin-cron-crew-report-email-verification"
      aria-label={`${action.param.label} verification`}
    >
      <form className="flex flex-wrap items-start gap-3" onSubmit={submit} noValidate>
        <div className="min-w-[240px] flex-1">
          <label htmlFor={inputId} className="sr-only">
            {action.param.label}
          </label>
          <input
            id={inputId}
            type="email"
            value={email}
            placeholder={action.param.placeholder}
            aria-invalid={showValidationError}
            aria-describedby={showValidationError ? errorId : undefined}
            className={twMerge(
              "h-10 w-full border bg-transparent px-3 text-sm outline-none focus:border-green",
              showValidationError ? "border-[#BF0002]" : "border-gray-300",
            )}
            onChange={(event) => setEmail(event.target.value)}
          />
          {showValidationError && (
            <p id={errorId} role="alert" className="mt-1 text-xs text-[#BF0002]">
              {ADMIN_CRON_CARGILL_EMAIL_ERROR}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isRunning || normalizedEmail === null}
          data-test={`admin-cron-run-${action.variant.endpoint}`}
          className={twMerge(
            adminStyles.button.primary,
            adminStyles.button.safe,
            adminStyles.button.disabled,
            "h-10 w-auto flex-none",
          )}
        >
          <PaperAirplaneIcon className="h-5 w-5" />
          {isSending ? "Sending..." : action.variant.label}
        </button>
      </form>
    </section>
  );
};

export default CronEmailVerificationAction;
